import { useCallback, useEffect, useRef, useState } from "react";
import storage from "./attemptStorage";

/**
 * Where a vendor checkout remembers its attempt, for the buyer and event it
 * was started for: only the attempt's id, so reopening the checkout (or the
 * app) picks the server's attempt up again instead of starting another.
 * Nothing else is kept: no details, cards or credentials. The key is the
 * one the earlier screen used, so an attempt it started resumes here.
 */
export const attemptKey = (uid: string, accessId: string) =>
  `buy-on-behalf:${uid}:${accessId}`;

/**
 * The attempt remembered under `key`, read once: `undefined` while it is
 * being read, then the id or `null`. The checkout mounts only once this is
 * known, and a later write (the checkout remembering the attempt it has
 * just started) is never read back into it. No key (`null`): nothing is
 * read, and nothing is remembered.
 */
export function useRestoredAttempt(
  key: string | null,
  disk: Pick<typeof storage, "getItem" | "setItem" | "removeItem"> = storage,
) {
  const [read, setRead] = useState<{ key: string; id: string | null } | null>(
    null,
  );
  useEffect(() => {
    if (key === null) return;
    let current = true;
    disk
      .getItem(key)
      .catch(() => null)
      .then((id) => {
        // Another buyer or event meanwhile: not this one's to resume.
        if (current) setRead({ key, id: id ?? null });
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [key, disk]);
  if (key === null) return null;
  return read?.key === key ? read.id : undefined;
}

/**
 * Remembers (or forgets, with `null`) the attempt under `key`, in the order
 * the checkout says so, and only while `key` is still the checkout's own:
 * once the buyer or event has changed, a late write is dropped.
 */
export function useAttemptWriter(
  key: string,
  disk: Pick<typeof storage, "getItem" | "setItem" | "removeItem"> = storage,
) {
  const live = useRef(key);
  useEffect(() => {
    live.current = key;
    return () => {
      live.current = "";
    };
  }, [key]);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  return useCallback(
    (id: string | null) => {
      if (live.current !== key) return;
      queue.current = queue.current
        .then(() => {
          // Asked again when its turn comes: the buyer or event may have
          // changed while an earlier write was still going.
          if (live.current !== key) return;
          return id ? disk.setItem(key, id) : disk.removeItem(key);
        })
        .catch(() => undefined);
    },
    [key, disk],
  );
}
